import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { boatApi } from '../services/boatApi';

// Debounce a fast-changing value so we don't fire a resolve request per keystroke.
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// Returns the canonical "did you mean?" suggestion for a typed brand value, or
// null. Only the backend's mid-band Suggest outcome yields a non-null name.
export function useBrandSuggestion(
  field: 'manufacturer' | 'model' | 'engine_manufacturer',
  value: string,
  manufacturer?: string,
): string | null {
  const trimmed = value.trim();
  const debounced = useDebouncedValue(trimmed, 350);

  const { data } = useQuery({
    queryKey: ['inventory', 'brand-suggestion', field, debounced, manufacturer ?? ''],
    queryFn: () => boatApi.resolveBrand(field, debounced, manufacturer),
    enabled: debounced.length >= 2,
    staleTime: 60_000,
  });

  return data?.suggestion ?? null;
}
